import React, { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Modal,
  Select,
  Table,
  message,
} from 'antd';
import axios from 'axios';
import AuthContext from '../../../services/Auth';
import { buildResourceAllocationPayload } from '../../../utils/resourceAllocationPayload';
import {
  formatSriLankaDate,
  formatSriLankaDateTime,
  toSriLankaMoment,
} from '../../../utils/sriLankaTime';
import { uniqueBy } from '../../../utils/uniqueOptions';
import {
  DELETE_ALLOCATION_URL,
  GET_ALLOCATIONS_URL,
  GET_ASSIGNED_USERS_URL,
  UPDATE_ALLOCATION_URL,
  masterHostKeyCode,
  subscribeKey,
} from '../../../constant';
import '../../../assets/css/resourceAllocation.scss';

const { RangePicker } = DatePicker;

const percentageOptions = Array.from({ length: 21 }, (_, index) => {
  const value = `${index * 5}%`;

  return {
    value,
    label: value,
  };
});

const parsePercentage = (value) => Number(String(value || '').replace('%', '').trim());

const formatPercentage = (value) => `${value}%`;

const normalizeAllocations = (result) => {
  if (!result) {
    return [];
  }

  if (Array.isArray(result)) {
    return result;
  }

  if (Array.isArray(result.data)) {
    return result.data;
  }

  return [];
};

const mapAllocationRow = (row) => ({
  key: `${row.userId ?? row.UserId}-${row.projectId ?? row.ProjectId}-${row.allocId ?? row.AllocId ?? ''}`,
  allocId: row.allocId ?? row.AllocId,
  userId: row.userId ?? row.UserId,
  userName: row.userName ?? row.UserName,
  projectId: row.projectId ?? row.ProjectId,
  projectName: row.projectName ?? row.ProjectName,
  startDate: row.startDate ?? row.StartDate,
  endDate: row.endDate ?? row.EndDate,
  billablePercentage: row.billablePercentage ?? row.BillablePercentage,
  nonBillablePercentage: row.nonBillablePercentage ?? row.NonBillablePercentage,
  allocatedBy: row.allocatedBy ?? row.AllocatedBy,
  allocatedByName: row.allocatedByName ?? row.AllocatedByName,
  allocatedAt: row.allocatedAt ?? row.AllocatedAt,
});

const validatePercentages = (billableValue, nonBillableValue) => {
  const billable = parsePercentage(billableValue);
  const nonBillable = parsePercentage(nonBillableValue);

  if ([billable, nonBillable].some((value) => Number.isNaN(value))) {
    return 'Please select valid percentages.';
  }

  if (billable < 0 || billable > 100) {
    return 'Billable percentage must be between 0% and 100%.';
  }

  if (nonBillable < 0 || nonBillable > 100) {
    return 'Non billable percentage must be between 0% and 100%.';
  }

  if (billable + nonBillable > 100) {
    return 'Billable and non-billable together must not exceed 100%.';
  }

  return null;
};

const ResourcesByPerson = () => {
  const [editForm] = Form.useForm();
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedInternalUserId, setSelectedInternalUserId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [allocatedByUserId, setAllocatedByUserId] = useState(null);
  const [hasViewedAllocations, setHasViewedAllocations] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const loggedInUser = AuthContext.getCachedUser();
  const loggedInUserEmail = (loggedInUser?.userName || loggedInUser?.profile?.unique_name || '')
    .toLowerCase();

  useEffect(() => {
    const fetchAssignedUsers = async () => {
      setLoadingUsers(true);

      try {
        const response = await axios.get(
          `${GET_ASSIGNED_USERS_URL}&allocatorEmail=${encodeURIComponent(loggedInUserEmail)}`
        );
        const payload = response.data;
        const data = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.users)
            ? payload.users
            : [];

        const allocatorId =
          payload?.allocatedBy ??
          payload?.AllocatedBy ??
          null;

        const uniqueData = uniqueBy(data, (user) => user.userId ?? user.UserId);

        const options = uniqueData.map((user) => ({
          value: user.userId ?? user.UserId,
          internalUserId: user.id ?? user.Id,
          label: user.userName ?? user.UserName,
          userEmail: user.userEmail ?? user.UserEmail,
        }));

        const resolvedAllocatorId =
          allocatorId ??
          options.find((user) => user.userEmail?.toLowerCase() === loggedInUserEmail)
            ?.internalUserId ??
          null;

        setAllocatedByUserId(resolvedAllocatorId);
        setPeopleOptions(options);
      } catch (error) {
        // console.error('Unable to load assigned users', error);
        message.error('Unable to load assigned users. Please refresh the page.');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAssignedUsers();
  }, [loggedInUserEmail]);

  const handlePersonChange = (personGuid) => {
    setSelectedPersonId(personGuid || null);

    const selectedPerson = peopleOptions.find((person) => person.value === personGuid);
    setSelectedInternalUserId(selectedPerson?.internalUserId ?? null);
    setTableData([]);
    setHasViewedAllocations(false);
    setSelectedRows([]);
  };

  const deleteAllocations = async (allocIds) => {
    if (!allocIds?.length) {
      return;
    }

    setDeleting(true);

    try {
      await Promise.all(
        allocIds.map((allocId) =>
          axios.delete(`${DELETE_ALLOCATION_URL}${encodeURIComponent(allocId)}?code=${masterHostKeyCode}${subscribeKey}`)
        )
      );

      message.success(
        allocIds.length === 1
          ? 'Allocation deleted successfully.'
          : `${allocIds.length} allocations deleted successfully.`
      );
      setSelectedRows([]);
      await loadAllocations();
    } catch (error) {
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to delete allocation(s).');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (allocIds) => {
    const count = allocIds.length;

    Modal.confirm({
      title: count === 1 ? 'Delete Allocation' : `Delete ${count} Allocations`,
      content:
        count === 1
          ? 'Are you sure you want to delete this allocation?'
          : `Are you sure you want to delete ${count} selected allocations?`,
      okText: 'Delete',
      okButtonProps: { danger: true, loading: deleting },
      onOk: () => deleteAllocations(allocIds),
    });
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map((row) => row.key),
    onChange: (_, selected) => setSelectedRows(selected),
  };

  const loadAllocations = async () => {
    if (!selectedInternalUserId) {
      message.warning('Please select a person first.');
      return;
    }

    setLoadingAllocations(true);

    try {
      const response = await axios.get(
        `${GET_ALLOCATIONS_URL}&userId=${encodeURIComponent(selectedInternalUserId)}`
      );
      const rows = normalizeAllocations(response.data).map(mapAllocationRow);
      setTableData(rows);
      setHasViewedAllocations(true);

      if (!rows.length) {
        message.info('No allocations found for this person.');
      }
    } catch (error) {
      // console.error('Unable to load allocations', error?.response?.data || error);
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to load allocations.');
      setTableData([]);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setEditModalOpen(true);

    editForm.setFieldsValue({
      allocationDateRange: [
        toSriLankaMoment(row.startDate),
        toSriLankaMoment(row.endDate),
      ],
      billablePercentage: formatPercentage(row.billablePercentage),
      nonBillablePercentage: formatPercentage(row.nonBillablePercentage),
    });
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    editForm.resetFields();
  };

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();

      if (!editingRow) {
        return;
      }

      if (!allocatedByUserId) {
        message.error('Unable to determine your user id. Please refresh the page.');
        return;
      }

      const percentageError = validatePercentages(
        values.billablePercentage,
        values.nonBillablePercentage
      );

      if (percentageError) {
        message.error(percentageError);
        return;
      }

      const [startDate, endDate] = values.allocationDateRange || [];

      if (!startDate || !endDate) {
        message.error('Please select the allocation date range.');
        return;
      }

     
      const updateBody = buildResourceAllocationPayload({
        allocId: editingRow.allocId,
        userId: editingRow.userId,
        projectId: editingRow.projectId,
        startDate,
        endDate,
        billablePercentage: parsePercentage(values.billablePercentage),
        nonBillablePercentage: parsePercentage(values.nonBillablePercentage),
        allocatedBy: allocatedByUserId,
      });


      

      setSavingEdit(true);

      await axios.put(UPDATE_ALLOCATION_URL, updateBody);

      message.success('Allocation updated successfully.');
      closeEditModal();
      await loadAllocations();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }

      
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to update allocation.');
    } finally {
      setSavingEdit(false);
    }
  };

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'projectName',
      key: 'projectName',
      ellipsis: true,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (value) => formatSriLankaDate(value),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (value) => formatSriLankaDate(value),
    },
    {
      title: 'Billable %',
      dataIndex: 'billablePercentage',
      key: 'billablePercentage',
      align: 'center',
      render: (value) => formatPercentage(value),
    },
    {
      title: 'Non Billable %',
      dataIndex: 'nonBillablePercentage',
      key: 'nonBillablePercentage',
      align: 'center',
      render: (value) => formatPercentage(value),
    },
    {
      title: 'Allocated By',
      dataIndex: 'allocatedByName',
      key: 'allocatedByName',
      ellipsis: true,
    },
    {
      title: 'Allocated At (SL)',
      dataIndex: 'allocatedAt',
      key: 'allocatedAt',
      render: (value) => formatSriLankaDateTime(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, row) => (
        <>
          <Button type="link" className="resource-allocation-edit-button" onClick={() => openEditModal(row)}>
            Edit
          </Button>
          <Button
            type="link"
            danger
            className="resource-allocation-delete-button"
            onClick={() => confirmDelete([row.allocId])}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="table-container resource-allocation-page">
      <div className="resource-allocation-shell">
        <div className="resource-allocation-toolbar">
          <div>
            <h2>Allocationsby Person</h2>
            <p>Select a team member to view and update their project allocations.</p>
          </div>
          <div className="resource-allocation-search">
            <Select
              showSearch
              allowClear
              placeholder="Select or type a person name"
              value={selectedPersonId}
              onChange={handlePersonChange}
              options={peopleOptions.map(({ value, label }) => ({ value, label }))}
              optionFilterProp="label"
              className="resource-allocation-person-select"
              loading={loadingUsers}
            />
            <Button
              type="primary"
              onClick={loadAllocations}
              className="resource-allocation-button"
              loading={loadingAllocations}
              disabled={!selectedInternalUserId}
            >
              View Allocations
            </Button>
          </div>
        </div>

        <div className="resource-allocation-table-wrap">
          <div className="resource-allocation-table-toolbar">
            <Button
              danger
              onClick={() => confirmDelete(selectedRows.map((row) => row.allocId))}
              disabled={!selectedRows.length}
              loading={deleting}
              className="resource-allocation-button"
            >
              Delete Selected{selectedRows.length ? ` (${selectedRows.length})` : ''}
            </Button>
          </div>
          <Table
            rowKey="key"
            columns={columns}
            dataSource={tableData}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            tableLayout="fixed"
            size="middle"
            loading={loadingAllocations}
            className="resource-allocation-table"
            rowSelection={rowSelection}
            locale={{
              emptyText: !selectedInternalUserId
                ? 'Select a person and click View Allocations.'
                : !hasViewedAllocations
                  ? 'Click View Allocations to load allocation details.'
                  : 'No allocations found for the selected person.',
            }}
          />
        </div>
      </div>

      <Modal
        title={editingRow ? `Edit Allocation — ${editingRow.projectName}` : 'Edit Allocation'}
        visible={editModalOpen}
        onCancel={closeEditModal}
        onOk={handleSaveEdit}
        okText="Save Changes"
        confirmLoading={savingEdit}
        destroyOnClose
        className="resource-allocation-edit-modal"
      >
        <Form form={editForm} layout="vertical" className="resource-allocation-edit-form">
          <Form.Item
            label="Allocation Date Range"
            name="allocationDateRange"
            rules={[{ required: true, message: 'Please select the allocation date range.' }]}
          >
            <RangePicker
              format="DD/MM/YYYY"
              className="resource-allocation-range"
              placeholder={['DD/MM/YYYY', 'DD/MM/YYYY']}
            />
          </Form.Item>

          <Form.Item
            label="Billable Percentage"
            name="billablePercentage"
            dependencies={['nonBillablePercentage']}
            rules={[
              { required: true, message: 'Please select the billable percentage.' },
              () => ({
                validator() {
                  const error = validatePercentages(
                    editForm.getFieldValue('billablePercentage'),
                    editForm.getFieldValue('nonBillablePercentage')
                  );

                  return error ? Promise.reject(new Error(error)) : Promise.resolve();
                },
              }),
            ]}
          >
            <Select
              showSearch
              options={percentageOptions}
              optionFilterProp="label"
              placeholder="Select billable percentage"
            />
          </Form.Item>

          <Form.Item
            label="Non Billable Percentage"
            name="nonBillablePercentage"
            dependencies={['billablePercentage']}
            rules={[
              { required: true, message: 'Please select the non billable percentage.' },
              () => ({
                validator() {
                  const error = validatePercentages(
                    editForm.getFieldValue('billablePercentage'),
                    editForm.getFieldValue('nonBillablePercentage')
                  );

                  return error ? Promise.reject(new Error(error)) : Promise.resolve();
                },
              }),
            ]}
          >
            <Select
              showSearch
              options={percentageOptions}
              optionFilterProp="label"
              placeholder="Select non billable percentage"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ResourcesByPerson;
